import React, {useState} from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import {Alarm, ArrowBack, Article, CheckCircle, Flight, FlightTakeoff, InfoOutlined, Lock} from '@mui/icons-material';
import {t} from 'ttag';
import {DateTime} from 'luxon';
import {useLocation} from 'react-router-dom';
import {IFlight, IPassenger} from '../../store/apis/interfaces/flight';
import useFadeNavigate from './useFadeNavigate';
import FlightStatusBadge from './FlightStatusBadge';

const fmt = (iso: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat('HH:mm') : '—';

const ataStaMinutes = (f: IFlight): number => {
  const sta = f.arrival?.scheduledTime;
  const ata = f.arrival?.actualTime ?? f.arrival?.estimatedTime;
  if (!sta || !ata) return 0;
  return Math.max(0, Math.round(DateTime.fromISO(ata).diff(DateTime.fromISO(sta), 'minutes').minutes));
};

const hasDelayReasons = (f: IFlight): boolean =>
  (f.departure.delays?.length ?? 0) + (f.arrival?.delays?.length ?? 0) > 0;

export type DeclarationType = 'delay' | 'boarding';

interface LocationState {
  flight: IFlight;
  carrier: string;
  flightNumber: string;
  date: string;
  selectedPassengers: IPassenger[];
}

const BOARDING_STEPS = [t`Declaration Type`, t`Recipient Details`];
const DELAY_STEPS = [t`Declaration Type`, t`Details`, t`Review`];

interface OptionProps {
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
  gradientA: string;
  gradientB: string;
  shadow: string;
  blocked?: boolean;
  blockedReason?: string;
}

const DeclarationOption = ({selected, onSelect, icon, title, description, gradientA, gradientB, shadow, blocked, blockedReason}: OptionProps) => (
  <Card
    variant='outlined'
    sx={{
      cursor: blocked ? 'not-allowed' : 'pointer',
      borderColor: blocked ? 'rgba(0,0,0,0.12)' : selected ? 'primary.main' : 'divider',
      borderWidth: selected ? 2 : 1,
      bgcolor: blocked ? 'action.disabledBackground' : selected ? 'action.hover' : 'background.paper',
      transition: 'all 0.15s ease',
      opacity: blocked ? 0.65 : 1,
      ...(!blocked && {'&:hover': {borderColor: 'primary.main', bgcolor: 'action.hover'}}),
    }}
    onClick={blocked ? undefined : onSelect}
  >
    <CardContent sx={{py: 1.75, px: 2.5, '&:last-child': {pb: blocked && blockedReason ? 1.25 : 1.75}, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
        background: blocked
          ? 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)'
          : `linear-gradient(135deg, ${gradientA} 0%, ${gradientB} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: blocked ? 'none' : `0 2px 8px ${shadow}`,
      }}>
        {blocked ? <Lock sx={{color: 'white', fontSize: '1.3rem'}} /> : icon}
      </Box>
      <Box sx={{flex: 1}}>
        <Typography fontWeight='bold' variant='body1' lineHeight={1.3} color={blocked ? 'text.disabled' : 'text.primary'}>{title}</Typography>
        <Typography variant='body2' color='text.disabled' sx={{mt: 0.25}}>{description}</Typography>
      </Box>
      {selected && !blocked && <CheckCircle sx={{color: 'primary.main', fontSize: '1.4rem', flexShrink: 0}} />}
      {blocked && (
        <Box sx={{
          width: '100%', display: 'flex', alignItems: 'flex-start', gap: 0.75,
          pt: 1, borderTop: '1px dashed', borderColor: 'rgba(237,108,2,0.35)',
        }}>
          <InfoOutlined sx={{color: 'warning.main', fontSize: '0.95rem', mt: '1px', flexShrink: 0}} />
          <Typography variant='caption' sx={{color: 'warning.dark', lineHeight: 1.5}}>
            {blockedReason}
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

const DeclarationTypePage = () => {
  const navigate = useFadeNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const [declarationType, setDeclarationType] = useState<DeclarationType | null>(() => {
    if (!state?.flight) return null;
    const f = state.flight;
    const delayMins = ataStaMinutes(f);
    const cancelled = (f.status ?? '').toLowerCase().includes('cancel');
    const atd = !!f.departure.actualTime;
    const delayAvailable = delayMins > 0 || hasDelayReasons(f);
    const boardingAvailable = atd && !cancelled;
    if (delayAvailable && !boardingAvailable) return 'delay';
    if (boardingAvailable && !delayAvailable) return 'boarding';
    return null;
  });

  if (!state?.flight) {
    return (
      <Box sx={{textAlign: 'center', mt: 4}}>
        <Typography color='error' gutterBottom>
          {t`No flight data. Please go back and search again.`}
        </Typography>
        <Button
          variant='text'
          startIcon={<ArrowBack />}
          onClick={() => navigate('/declarations/flight')}
          sx={{mt: 1, color: 'text.secondary'}}
        >
          {t`Back to Search`}
        </Button>
      </Box>
    );
  }

  const {flight, carrier, flightNumber, date, selectedPassengers} = state;

  const formattedDate = DateTime.fromFormat(date, 'yyyy-MM-dd').toLocaleString(DateTime.DATE_FULL);

  const allDelays = [
    ...(flight.departure.delays ?? []),
    ...(flight.arrival.delays ?? [])
  ].filter((d, i, arr) => arr.findIndex(x => x.code === d.code) === i);

  const totalDelayMins = ataStaMinutes(flight);
  const hasDelay = totalDelayMins > 0 || hasDelayReasons(flight);

  const isCancelled = (flight.status ?? '').toLowerCase().includes('cancel');
  const hasATD = !!flight.departure.actualTime;
  const hasATA = !!flight.arrival?.actualTime;
  const noShowPassengers = selectedPassengers.filter((p) => !p.boardingTime);
  const hasNoShows = noShowPassengers.length > 0;
  const hasMissingTicketNumbers = selectedPassengers.some((p) => !p.ticketNumber);
  const flightDateTime = flight.departure.scheduledTime
    ? DateTime.fromISO(flight.departure.scheduledTime)
    : DateTime.fromFormat(date, 'yyyy-MM-dd');
  const isOver24h = DateTime.now().diff(flightDateTime, 'hours').hours > 24;
  const showAwaitingInfo = isOver24h && !isCancelled && !hasATD && !hasATA;

  const passengerNames = selectedPassengers
    .map((p) => `${p.paxGivenName?.[0] ?? '?'}.${p.paxSurname || p.paxGivenName}`)
    .join('; ');

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
      {/* Flight header */}
      <Card
        elevation={2}
        sx={{borderTop: '3px solid', borderColor: hasDelay || showAwaitingInfo ? 'warning.main' : 'primary.main'}}
      >
        <CardContent sx={{py: 2, '&:last-child': {pb: 2}}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
            <Box sx={{
              background: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
              color: 'white',
              borderRadius: 2,
              width: 48,
              height: 48,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 3px 10px rgba(1,87,155,0.45)',
              gap: 0.25,
            }}>
              <Typography sx={{fontWeight: 900, fontSize: '0.9rem', letterSpacing: '0.1em', lineHeight: 1}}>
                {carrier.toUpperCase()}
              </Typography>
              <FlightTakeoff sx={{fontSize: '0.65rem', opacity: 0.75}} />
            </Box>

            <Box sx={{flex: 1, minWidth: 0}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                <Typography variant='h6' fontWeight='bold' lineHeight={1.2}>
                  {carrier.toUpperCase()}{flightNumber}
                </Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <Typography variant='body1' fontWeight='bold'>{flight.origin}</Typography>
                  <Flight sx={{fontSize: '1rem', color: 'text.secondary'}} />
                  <Typography variant='body1' fontWeight='bold'>{flight.destination}</Typography>
                </Box>
                <FlightStatusBadge status={flight.status} hasDelay={hasDelay} />
              </Box>
              <Typography variant='body2' color='text.secondary'>
                {formattedDate}
                {flight.departure.scheduledTime && ` · STD: ${fmt(flight.departure.scheduledTime)}`}
                {flight.departure.actualTime && ` · ATD: ${fmt(flight.departure.actualTime)}`}
              </Typography>
            </Box>

            {hasDelay && allDelays.length > 0 && (
              <Box
                sx={{
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  minWidth: 180,
                  flexShrink: 0
                }}
              >
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{fontWeight: 700, letterSpacing: '0.08em', display: 'block', mb: 0.5}}
                >
                  {allDelays.length > 1 ? t`DELAY REASONS` : t`DELAY REASON`}
                </Typography>
                {allDelays.map((d) => (
                  <Box key={d.code} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                    <Typography variant='body2' sx={{lineHeight: 1.8}}>
                      <Box component='span' sx={{color: 'primary.main', fontWeight: 700}}>{d.code}</Box>
                      {' – '}{d.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Passengers summary */}
      <Card variant='outlined' sx={{bgcolor: 'grey.50'}}>
        <CardContent sx={{py: 1.5, '&:last-child': {pb: 1.5}}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Article sx={{fontSize: '1.1rem', color: 'text.secondary'}} />
            <Typography variant='body2'>
              {t`Issue declaration for`}{' '}
              <Box component='span' fontWeight='bold'>{selectedPassengers.length}</Box>{' '}
              {t`passengers`}{': '}
              <Box component='span' fontWeight='bold'>{passengerNames}</Box>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Stepper */}
      <Stepper activeStep={0} alternativeLabel>
        {(declarationType === 'boarding' || !hasDelay ? BOARDING_STEPS : DELAY_STEPS).map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Declaration type card */}
      <Card elevation={3}>
        <CardContent sx={{p: 3}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
            <Article sx={{color: 'text.secondary'}} />
            <Typography variant='h6' fontWeight='bold'>{t`Declaration Type`}</Typography>
          </Box>
          <Typography variant='body2' color='text.secondary' sx={{mb: 3}}>
            {t`Select the type of declaration to issue for the selected passengers.`}
          </Typography>

          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
            {hasDelay && (
              <DeclarationOption
                selected={declarationType === 'delay'}
                onSelect={() => setDeclarationType('delay')}
                icon={<Alarm sx={{color: 'white', fontSize: '1.3rem'}} />}
                title={t`Delay Declaration`}
                description={`${t`Proof of`} ${totalDelayMins} ${t`minute delay`}`}
                gradientA='#fb8c00'
                gradientB='#e65100'
                shadow='rgba(230,81,0,0.4)'
                blocked={hasMissingTicketNumbers}
                blockedReason={hasMissingTicketNumbers ? t`Not available — one or more selected passengers have no ticket number on record, which is required to later verify the declaration's authenticity. Go back and select passengers with a recorded ticket number.` : undefined}
              />
            )}
            {!hasDelay && showAwaitingInfo && (
              <DeclarationOption
                selected={false}
                onSelect={() => {}}
                icon={<Alarm sx={{color: 'white', fontSize: '1.3rem'}} />}
                title={t`Delay Declaration`}
                description={t`Proof of flight delay`}
                gradientA='#fb8c00'
                gradientB='#e65100'
                shadow='rgba(230,81,0,0.4)'
                blocked
                blockedReason={t`Not yet available — this flight is still awaiting consolidated data. Check back once the actual times have been confirmed.`}
              />
            )}
            <DeclarationOption
              selected={declarationType === 'boarding'}
              onSelect={() => setDeclarationType('boarding')}
              icon={<FlightTakeoff sx={{color: 'white', fontSize: '1.3rem'}} />}
              title={t`Boarding Declaration`}
              description={t`Proof that the passenger boarded the flight`}
              gradientA='#00897b'
              gradientB='#00695c'
              shadow='rgba(0,105,92,0.4)'
              blocked={!hasATD || hasNoShows || hasMissingTicketNumbers}
              blockedReason={
                !hasATD
                  ? t`Not yet available — a Boarding Declaration requires a confirmed actual departure time (ATD). Check back once the flight has departed and the actual times have been updated.`
                  : hasNoShows
                    ? t`Not available — one or more selected passengers have no recorded boarding time, meaning they did not board this flight. Go back and select only passengers who actually boarded.`
                    : hasMissingTicketNumbers
                      ? t`Not available — one or more selected passengers have no ticket number on record, which is required to later verify the declaration's authenticity. Go back and select passengers with a recorded ticket number.`
                      : undefined
              }
            />
          </Box>
        </CardContent>
      </Card>

      <Divider />

      {/* Footer */}
      <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
        <Button variant='text' startIcon={<ArrowBack />} sx={{color: 'text.secondary'}} onClick={() => navigate(-1)}>
          {t`Back`}
        </Button>
        <Button
          variant='contained'
          disabled={!declarationType}
          onClick={() =>
            navigate(
              declarationType === 'boarding'
                ? '/declarations/flight/declaration/boarding'
                : '/declarations/flight/declaration/delay',
              {state: {...state, declarationType}}
            )
          }
        >
          {t`Continue`}
        </Button>
      </Box>
    </Box>
  );
};

export default DeclarationTypePage;
